import { useState, useEffect, useRef } from 'react';
import {
  uploadHvAbrechnung, extractHvAbrechnung, listHvAbrechnungen, getHvAbrechnung,
  updateHvAbrechnung, deleteHvAbrechnung, applyHvAbrechnung, getProperties, getUnits, getKostenstellen,
} from '../services/api';
import api from '../services/api';
import {
  Receipt, Upload, Robot, Trash, X, ArrowRight, Buildings, Calendar,
  Check, Warning, ListBullets, CurrencyEur, FileArrowUp, Spinner, FilePdf, Image as ImgIcon, MagicWand,
} from '@phosphor-icons/react';

const STATUS_BADGES = {
  ocr_done: { label: 'OCR fertig', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  extracted: { label: 'KI extrahiert', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  extract_failed: { label: 'KI-Fehler', cls: 'bg-red-50 text-red-700 border-red-200' },
  applied: { label: 'Gebucht', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
};

function eur(n) {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
}

export default function HVAbrechnung() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [openDetail, setOpenDetail] = useState(null);
  const fileRef = useRef(null);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [kostenstellen, setKostenstellen] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, p, u, k] = await Promise.all([listHvAbrechnungen(), getProperties(), getUnits(), getKostenstellen()]);
      setItems(r.data);
      setProperties(p.data);
      setUnits(u.data);
      setKostenstellen(k.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const res = await uploadHvAbrechnung(fd);
      // immediately trigger AI extract
      setOpenDetail({ ...res.data, _extracting: true });
      extractHvAbrechnung(res.data.id).then(r2 => {
        setOpenDetail(r2.data);
        load();
      }).catch(err => {
        alert('KI-Extraktion fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
        load();
      });
    } catch (e) {
      alert('Upload fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async (id, label) => {
    if (!window.confirm(`HV-Abrechnung "${label}" wirklich löschen?\nDazugehörige Buchungen werden ebenfalls entfernt.`)) return;
    try { await deleteHvAbrechnung(id); load(); }
    catch (e) { alert('Fehler: ' + (e.response?.data?.detail || e.message)); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="hv-abrechnung-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-slate-900">HV-Abrechnungs-Import</h1>
          <p className="text-slate-500 mt-1 text-sm">Hausverwalter-/WEG-Abrechnung hochladen, KI extrahiert die Positionen, du buchst per Klick auf deine Immobilie.</p>
        </div>
        <div>
          <input ref={fileRef} type="file" multiple accept="application/pdf,image/*" onChange={handleUpload} className="hidden" data-testid="hv-file-input" />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="bg-slate-900 text-white px-5 py-2.5 font-medium hover:bg-slate-800 inline-flex items-center gap-2 rounded-lg disabled:opacity-50"
            data-testid="hv-upload-button"
          >
            {uploading ? <Spinner weight="bold" className="w-4 h-4 animate-spin" /> : <FileArrowUp weight="bold" className="w-4 h-4" />}
            {uploading ? 'Wird hochgeladen…' : 'PDF/Bilder hochladen'}
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 p-10 text-center rounded-lg">
          <Receipt weight="light" className="w-14 h-14 text-slate-300 mx-auto mb-3" />
          <h3 className="font-heading text-lg font-bold text-slate-900 mb-1">Noch keine HV-Abrechnung hochgeladen</h3>
          <p className="text-slate-500 text-sm mb-4">Lade dein erstes Hausverwalter-Schreiben hoch — KI extrahiert die Positionen automatisch.</p>
          <button onClick={() => fileRef.current?.click()} className="text-violet-700 underline font-medium">PDF/Bild auswählen</button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3">Datei / Hausverwalter</th>
                  <th className="text-left px-4 py-3">Objekt / Jahr</th>
                  <th className="text-right px-4 py-3">Umlagefähig</th>
                  <th className="text-right px-4 py-3">Nicht umlag.</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map(h => {
                  const sb = STATUS_BADGES[h.status] || { label: h.status, cls: 'bg-slate-100 text-slate-700 border-slate-200' };
                  return (
                    <tr key={h.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{h.filename}</div>
                        <div className="text-xs text-slate-500">{h.vendor || '—'} · {h.page_count} Seiten</div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {h.property_address || '—'}
                        {h.period_year && <div className="text-xs text-slate-500">{h.period_year}</div>}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{eur(h.total_umlagefaehig)}</td>
                      <td className="px-4 py-3 text-right font-mono">{eur(h.total_nicht_umlagefaehig)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded border ${sb.cls}`}>{sb.label}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setOpenDetail(h)} className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1 mr-1" data-testid={`open-hv-${h.id}`}>
                          Details <ArrowRight weight="bold" className="w-3 h-3" />
                        </button>
                        <button onClick={() => handleDelete(h.id, h.filename)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`delete-hv-${h.id}`}>
                          <Trash weight="bold" className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {openDetail && (
        <HVDetailModal
          hv={openDetail}
          properties={properties}
          units={units}
          kostenstellen={kostenstellen}
          onClose={() => setOpenDetail(null)}
          onChanged={(updated) => { setOpenDetail(updated); load(); }}
        />
      )}

      {/* Learnings */}
      <HVLearningsSection kostenstellen={kostenstellen} />
    </div>
  );
}

function HVLearningsSection({ kostenstellen }) {
  const [open, setOpen] = useState(false);
  const [learnings, setLearnings] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/hv-abrechnungen/learnings');
      setLearnings(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const handleDelete = async (id) => {
    if (!window.confirm('Lernregel löschen?')) return;
    try { await api.delete(`/hv-abrechnungen/learnings/${id}`); load(); }
    catch (e) { alert('Fehler: ' + (e.response?.data?.detail || e.message)); }
  };

  const ksName = (id) => kostenstellen.find(k => k.id === id)?.name || '—';

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50" data-testid="hv-learnings-toggle">
        <div className="text-left">
          <h3 className="font-heading text-sm font-bold text-slate-900 inline-flex items-center gap-2">
            <MagicWand weight="bold" className="w-4 h-4 text-violet-600" />
            Auto-Mapping Lernregeln {open ? '' : '(klicken zum Öffnen)'}
          </h3>
          <p className="text-xs text-slate-500">Wenn du in einer HV-Abrechnung eine Kostenstelle manuell zuordnest, merkt sich das System die Zuordnung. Beim nächsten Upload wird sie automatisch angewendet.</p>
        </div>
        <span className="text-slate-400">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="border-t border-slate-200 p-5">
          {loading ? (
            <div className="text-center py-6"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900 mx-auto"></div></div>
          ) : learnings.length === 0 ? (
            <p className="text-center text-sm text-slate-500 py-4">Noch keine Lernregeln vorhanden. Ändere in einer Abrechnung die Kostenstelle einer Position manuell — die Regel wird automatisch gespeichert.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Erkannter Text</th>
                    <th className="text-left px-3 py-2">→ Kostenstelle</th>
                    <th className="text-center px-3 py-2 w-20">Anwendungen</th>
                    <th className="text-right px-3 py-2 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {learnings.map(l => (
                    <tr key={l.id} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-slate-900">{l.sample_text}</td>
                      <td className="px-3 py-2 text-slate-700">{l.kostenstelle_name || ksName(l.kostenstelle_id)}</td>
                      <td className="px-3 py-2 text-center font-mono text-xs">{l.count}×</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => handleDelete(l.id)} className="text-slate-400 hover:text-red-600" data-testid={`del-learning-${l.id}`}>
                          <Trash weight="bold" className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HVDetailModal({ hv, properties, units, kostenstellen, onClose, onChanged }) {
  const [data, setData] = useState(hv);
  const [savingApply, setSavingApply] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [applyForm, setApplyForm] = useState({
    property_id: hv.applied_property_id || '',
    unit_id: hv.applied_unit_id || '',
    year: hv.period_year || hv.applied_year || new Date().getFullYear() - 1,
    mode: hv.applied_mode || 'detailed',
  });

  useEffect(() => { setData(hv); }, [hv]);

  const unitsForProp = applyForm.property_id ? units.filter(u => u.property_id === applyForm.property_id) : [];

  const updatePosition = (idx, key, value) => {
    setData(prev => {
      const positions = [...(prev.positions || [])];
      const wasAuto = !!positions[idx]?.auto_mapped;
      positions[idx] = { ...positions[idx], [key]: value };
      // wenn User die Kostenstelle manuell ändert: Auto-Badge entfernen + lernen
      if (key === 'kostenstelle_id') {
        positions[idx].auto_mapped = false;
        const name = positions[idx].name;
        if (value && name && name.trim().length >= 3) {
          // fire and forget
          api.post('/hv-abrechnungen/learn-mapping', { position_name: name, kostenstelle_id: value })
            .catch(() => {});
        }
        // suppress unused warning
        void wasAuto;
      }
      return { ...prev, positions };
    });
  };

  const removePosition = (idx) => {
    setData(prev => {
      const positions = [...(prev.positions || [])];
      positions.splice(idx, 1);
      return { ...prev, positions };
    });
  };

  const addPosition = () => {
    setData(prev => ({ ...prev, positions: [...(prev.positions || []), { name: '', amount: 0, umlagefaehig: true }] }));
  };

  const handleSaveCorrections = async () => {
    try {
      const res = await updateHvAbrechnung(hv.id, {
        vendor: data.vendor,
        period_year: data.period_year ? Number(data.period_year) : null,
        property_address: data.property_address,
        total_umlagefaehig: data.total_umlagefaehig ? Number(data.total_umlagefaehig) : null,
        total_nicht_umlagefaehig: data.total_nicht_umlagefaehig ? Number(data.total_nicht_umlagefaehig) : null,
        positions: (data.positions || []).map(p => ({
          ...p,
          amount: typeof p.amount === 'string' ? parseFloat(p.amount.replace(',', '.')) : p.amount,
        })),
      });
      onChanged(res.data);
    } catch (e) { alert('Fehler: ' + (e.response?.data?.detail || e.message)); }
  };

  const handleRetryExtract = async () => {
    setRetrying(true);
    try {
      const res = await extractHvAbrechnung(hv.id);
      onChanged(res.data);
    } catch (e) {
      alert('KI-Extraktion fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    } finally { setRetrying(false); }
  };

  const handleApply = async () => {
    if (!applyForm.property_id) { alert('Bitte Immobilie wählen'); return; }
    if (!applyForm.year) { alert('Bitte Jahr eingeben'); return; }
    if (hasBlocking) {
      const ok = window.confirm('⚠ Es gibt Plausibilitäts-Warnungen (Summen weichen ab).\n\nTrotzdem buchen?');
      if (!ok) return;
    }
    await handleSaveCorrections();
    setSavingApply(true);
    try {
      const res = await applyHvAbrechnung(hv.id, {
        property_id: applyForm.property_id,
        unit_id: applyForm.unit_id || null,
        year: Number(applyForm.year),
        mode: applyForm.mode,
      });
      alert(`✓ ${res.data.transactions_created} Buchungen wurden angelegt.\n\nDu findest sie unter „Transaktionen" und in der Jahresabrechnung ${applyForm.year}.`);
      onChanged(res.data.hv_abrechnung);
      onClose();
    } catch (e) {
      alert('Fehler beim Buchen: ' + (e.response?.data?.detail || e.message));
    } finally { setSavingApply(false); }
  };

  const positionsSum = (data.positions || []).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const umlagefSum = (data.positions || []).filter(p => p.umlagefaehig).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const nichtUmlagefSum = positionsSum - umlagefSum;

  // Plausibility checks
  const tolerance = 1.0; // €
  const checks = [];
  const totalUmHv = parseFloat(data.total_umlagefaehig) || 0;
  const totalNUmHv = parseFloat(data.total_nicht_umlagefaehig) || 0;
  const totalHvSum = totalUmHv + totalNUmHv;
  if ((data.positions || []).length === 0) {
    checks.push({ level: 'warning', text: 'Keine Positionen erkannt. KI-Extraktion erneut versuchen oder manuell ergänzen.' });
  }
  if (totalUmHv > 0 && Math.abs(umlagefSum - totalUmHv) > tolerance) {
    checks.push({
      level: 'warning',
      text: `Summe umlagefähiger Positionen (${eur(umlagefSum)}) weicht von HV-Total (${eur(totalUmHv)}) ab — Differenz ${eur(umlagefSum - totalUmHv)}.`,
    });
  }
  if (totalNUmHv > 0 && Math.abs(nichtUmlagefSum - totalNUmHv) > tolerance) {
    checks.push({
      level: 'warning',
      text: `Summe nicht-umlagefähiger Positionen (${eur(nichtUmlagefSum)}) weicht von HV-Total (${eur(totalNUmHv)}) ab — Differenz ${eur(nichtUmlagefSum - totalNUmHv)}.`,
    });
  }
  if (totalHvSum > 0 && Math.abs(positionsSum - totalHvSum) > tolerance) {
    checks.push({
      level: 'info',
      text: `Gesamtsumme Positionen (${eur(positionsSum)}) vs. HV-Gesamtsumme (${eur(totalHvSum)}): Differenz ${eur(positionsSum - totalHvSum)}.`,
    });
  }
  const positionsWithoutKs = (data.positions || []).filter(p => !p.kostenstelle_id).length;
  if (positionsWithoutKs > 0 && applyForm.mode === 'detailed') {
    checks.push({
      level: 'info',
      text: `${positionsWithoutKs} Position(en) ohne Kostenstellen-Zuordnung — fallen im Modus "Detailliert" in die Default-Kategorie.`,
    });
  }
  const hasBlocking = checks.some(c => c.level === 'warning');

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="hv-detail-modal">
      <div className="bg-white w-full max-w-4xl rounded-lg overflow-hidden flex flex-col max-h-[92vh]">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-900 text-white">
          <div>
            <h2 className="font-heading text-xl font-bold">HV-Abrechnung Details</h2>
            <p className="text-xs text-slate-300">{hv.filename} · {hv.page_count} Seiten</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X weight="bold" className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {hv._extracting && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800 inline-flex items-center gap-2">
              <Spinner weight="bold" className="w-4 h-4 animate-spin" /> KI extrahiert gerade die Positionen…
            </div>
          )}
          {data.ai_error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800 flex items-start gap-2">
              <Warning weight="bold" className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-bold">KI-Extraktion fehlgeschlagen</p>
                <p className="text-xs">{data.ai_error}</p>
                <button onClick={handleRetryExtract} disabled={retrying} className="mt-2 text-xs px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="retry-extract">
                  <Robot weight="bold" className="w-3 h-3" /> {retrying ? 'Läuft…' : 'Erneut versuchen'}
                </button>
              </div>
            </div>
          )}

          {/* Header info */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <FieldEdit label="Hausverwalter" value={data.vendor || ''} onChange={(v) => setData(p => ({ ...p, vendor: v }))} />
            <FieldEdit label="Jahr" type="number" value={data.period_year || ''} onChange={(v) => setData(p => ({ ...p, period_year: v }))} />
            <FieldEdit label="Objekt-Adresse" cls="sm:col-span-2" value={data.property_address || ''} onChange={(v) => setData(p => ({ ...p, property_address: v }))} />
          </div>

          {/* Totals */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <h3 className="font-heading text-sm font-bold text-slate-900 mb-3 inline-flex items-center gap-2"><CurrencyEur weight="bold" className="w-4 h-4" /> Summen</h3>
            <div className="grid grid-cols-2 gap-4">
              <FieldEdit label="Umlagefähig (lt. HV)" prefix="€" inputMode="decimal" value={data.total_umlagefaehig ?? ''} onChange={(v) => setData(p => ({ ...p, total_umlagefaehig: v }))} />
              <FieldEdit label="Nicht umlagefähig (lt. HV)" prefix="€" inputMode="decimal" value={data.total_nicht_umlagefaehig ?? ''} onChange={(v) => setData(p => ({ ...p, total_nicht_umlagefaehig: v }))} />
            </div>
            {data.positions?.length > 0 && (
              <p className="text-xs text-slate-500 mt-3">Summe Positionen: <span className="font-mono font-bold">{eur(positionsSum)}</span> · davon umlagefähig: <span className="font-mono font-bold">{eur(umlagefSum)}</span></p>
            )}
          </div>

          {/* Positions */}
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <h3 className="font-heading text-sm font-bold text-slate-900 inline-flex items-center gap-2"><ListBullets weight="bold" className="w-4 h-4" /> Positionen ({data.positions?.length || 0})</h3>
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    try {
                      const res = await api.post(`/hv-abrechnungen/${hv.id}/remap`);
                      onChanged(res.data.hv_abrechnung);
                      alert(res.data.message);
                    } catch (e) { alert('Fehler: ' + (e.response?.data?.detail || e.message)); }
                  }}
                  className="text-xs px-2 py-1 border border-violet-200 bg-violet-50 text-violet-700 rounded hover:bg-violet-100 inline-flex items-center gap-1"
                  title="Kostenstellen-Zuordnung erneut berechnen"
                  data-testid="hv-remap-button"
                >
                  <MagicWand weight="bold" className="w-3 h-3" /> Auto-Mapping
                </button>
                <button onClick={addPosition} className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-50">+ Position</button>
              </div>
            </div>
            <div className="border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Bezeichnung</th>
                    <th className="text-left px-3 py-2 w-48">Kostenstelle</th>
                    <th className="text-right px-3 py-2 w-32">Betrag</th>
                    <th className="text-center px-3 py-2 w-24">Umlag?</th>
                    <th className="text-right px-3 py-2 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {(data.positions || []).map((p, idx) => (
                    <tr key={idx} className="border-t border-slate-100">
                      <td className="px-3 py-1.5">
                        <input value={p.name || ''} onChange={e => updatePosition(idx, 'name', e.target.value)} className="w-full bg-transparent border-b border-transparent focus:border-slate-400 outline-none text-sm py-1" />
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="flex items-center gap-1">
                          <select
                            value={p.kostenstelle_id || ''}
                            onChange={e => updatePosition(idx, 'kostenstelle_id', e.target.value || null)}
                            className="flex-1 bg-transparent border-b border-transparent focus:border-slate-400 outline-none text-xs py-1"
                            data-testid={`hv-pos-ks-${idx}`}
                          >
                            <option value="">— keine —</option>
                            {kostenstellen.map(k => (
                              <option key={k.id} value={k.id}>{k.name}</option>
                            ))}
                          </select>
                          {p.auto_mapped && (
                            <span title="Automatisch zugeordnet" className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200">
                              auto
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-1.5">
                        <input value={p.amount ?? ''} onChange={e => updatePosition(idx, 'amount', e.target.value)} inputMode="decimal" className="w-full text-right font-mono bg-transparent border-b border-transparent focus:border-slate-400 outline-none text-sm py-1" />
                      </td>
                      <td className="text-center">
                        <input type="checkbox" checked={!!p.umlagefaehig} onChange={e => updatePosition(idx, 'umlagefaehig', e.target.checked)} />
                      </td>
                      <td className="text-right pr-2">
                        <button onClick={() => removePosition(idx)} className="text-slate-400 hover:text-red-600 p-1"><Trash weight="bold" className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                  ))}
                  {(data.positions || []).length === 0 && (
                    <tr><td colSpan="5" className="text-center text-xs text-slate-500 py-4">Noch keine Positionen — KI hat nichts gefunden, bitte manuell hinzufügen oder Re-Extract.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {data.status !== 'extracted' && data.status !== 'applied' && data.status !== 'extract_failed' && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800 inline-flex items-center gap-2">
              <Spinner weight="bold" className="w-4 h-4 animate-spin" /> Verarbeitung läuft…
            </div>
          )}

          {/* Plausibility checks */}
          {checks.length > 0 && (
            <div className="space-y-2" data-testid="hv-plausibility-checks">
              {checks.map((c, i) => (
                <div key={i} className={`border rounded p-3 text-sm flex items-start gap-2 ${c.level === 'warning' ? 'bg-amber-50 border-amber-300 text-amber-900' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
                  <Warning weight="bold" className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p>{c.text}</p>
                </div>
              ))}
            </div>
          )}

          {/* Apply Section */}
          <div className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-4 space-y-3">
            <h3 className="font-heading text-base font-bold text-slate-900 inline-flex items-center gap-2"><Buildings weight="bold" className="w-5 h-5" /> Auf Immobilie buchen</h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-slate-600">Objekt *</label>
                <select value={applyForm.property_id} onChange={e => setApplyForm(p => ({ ...p, property_id: e.target.value, unit_id: '' }))} className="w-full border-b border-slate-300 bg-transparent py-2 outline-none" data-testid="hv-apply-property">
                  <option value="">— wählen —</option>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-slate-600">Einheit (optional)</label>
                <select value={applyForm.unit_id} onChange={e => setApplyForm(p => ({ ...p, unit_id: e.target.value }))} className="w-full border-b border-slate-300 bg-transparent py-2 outline-none" data-testid="hv-apply-unit">
                  <option value="">— gesamte Liegenschaft —</option>
                  {unitsForProp.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-slate-600">Jahr *</label>
                <input type="number" value={applyForm.year} onChange={e => setApplyForm(p => ({ ...p, year: e.target.value }))} className="w-full border-b border-slate-300 bg-transparent py-2 outline-none" data-testid="hv-apply-year" />
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider font-bold text-slate-600 mb-2">Übernahme-Modus</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className={`border-2 rounded-lg p-3 cursor-pointer ${applyForm.mode === 'detailed' ? 'border-emerald-600 bg-white' : 'border-slate-200 bg-white/50'}`}>
                  <input type="radio" name="apply-mode" value="detailed" checked={applyForm.mode === 'detailed'} onChange={e => setApplyForm(p => ({ ...p, mode: e.target.value }))} className="mr-2" data-testid="hv-mode-detailed" />
                  <span className="font-bold text-sm">Detailliert übernehmen</span>
                  <p className="text-xs text-slate-600 mt-0.5">Jede Position einzeln als Buchung mit Kostenstelle. Mieter-Abrechnung verteilt exakt nach BetrKV-Schlüssel.</p>
                </label>
                <label className={`border-2 rounded-lg p-3 cursor-pointer ${applyForm.mode === 'totals' ? 'border-emerald-600 bg-white' : 'border-slate-200 bg-white/50'}`}>
                  <input type="radio" name="apply-mode" value="totals" checked={applyForm.mode === 'totals'} onChange={e => setApplyForm(p => ({ ...p, mode: e.target.value }))} className="mr-2" data-testid="hv-mode-totals" />
                  <span className="font-bold text-sm">Nur Totale übernehmen</span>
                  <p className="text-xs text-slate-600 mt-0.5">Nur 2 Sammelbuchungen (umlagefähig + nicht umlagefähig). Mieter-Abrechnung umlegt anteilig nach Fläche.</p>
                </label>
              </div>
            </div>

            {data.applied_at && (
              <div className="text-xs bg-violet-50 border border-violet-200 rounded p-2 text-violet-700">
                <Check weight="bold" className="w-3.5 h-3.5 inline mr-1" /> Bereits gebucht ({data.applied_transaction_ids?.length || 0} Transaktionen). Erneutes „Buchen" überschreibt die alten Buchungen.
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-white flex flex-wrap gap-2 justify-end">
          <button onClick={handleSaveCorrections} className="px-4 py-2 border border-slate-200 rounded hover:bg-slate-50 text-sm" data-testid="hv-save-corrections">
            Korrekturen speichern
          </button>
          <button onClick={handleApply} disabled={savingApply || !applyForm.property_id} className="px-5 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded font-medium inline-flex items-center gap-2 disabled:opacity-50" data-testid="hv-apply-button">
            {savingApply ? <Spinner weight="bold" className="w-4 h-4 animate-spin" /> : <Buildings weight="bold" className="w-4 h-4" />}
            {savingApply ? 'Wird gebucht…' : 'Buchen'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldEdit({ label, value, onChange, type = 'text', inputMode, prefix, cls = '' }) {
  return (
    <div className={cls}>
      <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-1">{label}</label>
      <div className="flex items-center gap-1">
        {prefix && <span className="text-slate-500">{prefix}</span>}
        <input
          type={type}
          inputMode={inputMode}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border-b border-slate-300 bg-transparent py-2 outline-none focus:border-slate-900"
        />
      </div>
    </div>
  );
}
